import { Component, ErrorInfo, ReactNode } from 'react';
import { mapCognitoError } from '../utils/cognitoErrorMapper';
import { isChunkLoadError } from '../utils/staleChunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isAuthError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, isAuthError: false };
  }

  private isAuthenticationError(error: Error): boolean {
    // A chunk error's message carries the chunk URL, which could contain one of
    // the words below; it is never an auth problem.
    if (isChunkLoadError(error)) return false;
    const msg = error.message?.toLowerCase() || '';
    return ['unauthorized','authentication','token','session','cognito','sign in','login']
      .some(k => msg.includes(k));
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    const isAuthError = this.isAuthenticationError(error);
    this.setState({ error, errorInfo, isAuthError });
    if (isAuthError) {
      setTimeout(() => { window.location.href = '/signin'; }, 3000);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null, isAuthError: false });
  };

  // React.lazy keeps a failed chunk import rejected, so resetting state would
  // re-throw it; a reload fetches the current build instead.
  handleReload = (): void => { window.location.reload(); };

  handleSignIn = (): void => { window.location.href = '/signin'; };

  render(): ReactNode {
    if (this.state.hasError) {
      const { isAuthError, error } = this.state;
      const isChunkError = isChunkLoadError(error);
      const errorMessage = error ? mapCognitoError(error) : 'An unexpected error occurred';

      return (
        <div className="flex justify-center items-center min-h-screen bg-[var(--background)] p-6">
          <div className="bg-[var(--card)] border border-[var(--border)] rounded-[10px] shadow-lg p-8 max-w-[600px] text-center">
            {/* Icon */}
            <div className="mb-4">
              {isAuthError ? (
                <svg className="w-16 h-16 mx-auto text-[var(--gold-500)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              ) : (
                <svg className="w-16 h-16 mx-auto text-[var(--destructive)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              )}
            </div>

            <h2 className="text-2xl font-semibold text-[var(--foreground)] mb-2">
              {isAuthError ? 'Authentication Error' : 'Something went wrong'}
            </h2>
            <p className="text-sm text-[var(--muted-foreground)] mb-4">
              {isAuthError
                ? 'Your session has expired or you are not authenticated. You will be redirected to sign in.'
                : 'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.'}
            </p>

            {error && (
              <div className="mt-2 mb-4 p-3 bg-[var(--background)] rounded-lg text-left">
                <pre className="text-sm text-[var(--foreground)] whitespace-pre-wrap">{errorMessage}</pre>
              </div>
            )}

            <button
              onClick={isAuthError ? this.handleSignIn : isChunkError ? this.handleReload : this.handleReset}
              className="px-4 py-2 bg-[var(--primary-500)] text-white rounded-lg font-medium text-sm transition-all duration-150 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--primary-500)] focus:ring-offset-2"
            >
              {isAuthError ? 'Sign In' : isChunkError ? 'Reload page' : 'Try Again'}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
